import { Navigate, useParams, type RouteObject } from 'react-router-dom';
import { RoleGuard } from '@/shared/guards/RoleGuard';
import { CONTENT_ROLES } from '@/shared/guards/roles';
import { ContentPage } from './pages/ContentPage';
import { ContentItemDetailPage } from './pages/ContentItemDetailPage';
import { SubjectViewDetailPage } from './pages/SubjectViewDetailPage';
import { SubjectGraphPage } from './pages/SubjectGraphPage';
import { ExamPreviewPage } from './pages/ExamPreviewPage';
import { ExamImportPage } from './pages/ExamImportPage';
import { ExamSearchPage } from './pages/ExamSearchPage';
import { QuestionSearchPage } from './pages/QuestionSearchPage';

/**
 * Sub-árvore de rotas do módulo Conteúdo. Gateada por `CONTENT_ROLES` — espelha
 * o `@Roles('adminDashboard.administrator')` do `ContentAdminReadController`.
 * Dentro do módulo só a taxonomia tem gate próprio (`useCanWriteSubjects`,
 * espelhando o `StrictRoles` do backend). É só UX; o back enforce em
 * `/admin/content/*`.
 */
export const contentRoutes: RouteObject[] = [
  {
    element: <RoleGuard roles={CONTENT_ROLES} />,
    children: [
      {
        path: 'content',
        children: [
          // `:tab?` opcional: a aba vive na URL (compartilhável, sobrevive a
          // refresh). Rotas estáticas (`graph`) têm precedência sobre o param.
          { path: ':tab?', element: <ContentPage /> },
          { path: 'graph', element: <SubjectGraphPage /> },
          { path: 'exams/import', element: <ExamImportPage /> },
          // Segmento estático: ganha do `exams/:examId`, como o `import`.
          { path: 'exams/search', element: <ExamSearchPage /> },
          { path: 'exams/:examId', element: <ExamPreviewPage /> },
          { path: 'questions/search', element: <QuestionSearchPage /> },
          { path: 'items/:contentId', element: <ContentItemDetailPage /> },
          {
            path: 'questions/:questionId/official-comment',
            element: <OfficialCommentRedirect />,
          },
          { path: 'views/:viewId', element: <SubjectViewDetailPage /> },
        ],
      },
    ],
  },
];

/**
 * A página do comentário oficial virou o painel do comentário na ficha da
 * questão. A rota fica para o link que já foi colado num ticket ou no Plane.
 */
function OfficialCommentRedirect() {
  const { questionId = '' } = useParams<{ questionId: string }>();
  return <Navigate to={`/content/items/${questionId}`} replace />;
}
